/* eslint-disable max-len */
import { BasketReferenceType } from '@1po/1po-bff-fe-spec/generated/basket/request/model/BasketReferenceType';
import {
  RemoveExternalBasketReferencesDetail,
  RemoveVehicleReferencesDetail,
} from '@1po/1po-bff-fe-spec/generated/basket/request/RemoveReferences';
import { Origin, ReferenceSource } from '@1po/1po-bff-fe-spec/generated/basket/request/UpdateReferenceQuantity';
import { ExpiredReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/basket/response/BasketResponse';
import { ExternalBasketSection } from '@1po/1po-bff-fe-spec/generated/basket/response/model/ExternalBasketSection';
import { OtherSection } from '@1po/1po-bff-fe-spec/generated/basket/response/model/OtherSection';
import { CatalogSource, Reference } from '@1po/1po-bff-fe-spec/generated/basket/response/model/Reference';
import { ReferenceErrorRow } from '@1po/1po-bff-fe-spec/generated/basket/response/model/ReferenceErrorRow';
import { ReferenceType, VehicleSection } from '@1po/1po-bff-fe-spec/generated/basket/response/model/VehicleSection';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import {
  ADD_BASKET_OTHER_REFERENCE_RESPONSE,
  BASKET_ATTACH_OTHER_REFERENCE_CAR_NONCOMPATIBLE_RESPONSE,
  BASKET_ATTACH_OTHER_REFERENCE_CAR_RESPONSE,
  BASKET_INVALID_REFERENCE_QUANTITY_UPDATE_RESPONSE,
  GET_USER_BASKET_RESPONSE,
  UPLOAD_BASKET_REFERENCES_FILE_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { OrderDeliveryType } from '@1po/1po-bff-fe-spec/generated/order/request/CheckoutOrderRequest';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/model/TireItem';
import { createAction, createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { ModalStatusType, NO_FILE } from 'components/FileUpload/paramsByState';
import { isCertasOil } from 'components/StockInfo/catalogStockUtils';
import {
  ADD_OTHER_SECTION_REFERENCE,
  ADD_OTHER_SECTION_REFERENCES,
  ADD_REFERENCE_BY_REF_NUMBER,
  ADD_REFERENCE_BY_REF_NUMBER_UNDER_VEHICLE,
  ADD_REFERENCE_FROM_EASY_BASKET,
  ADD_TIRE_REFERENCE,
  ADD_VEHICLE_REFERENCES,
  ATTACH_OTHER_NON_APPLICABLE_REFERENCE,
  ATTACH_OTHER_REFERENCE,
  BASKET_NAMESPACE,
  BasketActionStatus,
  CrossSellingAddToBasketTrackedPayload,
  CROSS_SELLING_ADD_TO_BASKET_TRACKED,
  BasketState,
  CERTAS_OIL_REFERENCE_QUANTITY_LIMIT,
  ExternalBasketSectionLocal,
  FETCH_BASKET,
  NOTIFY_USER_ABOUT_EXPIRED_DISCOUNTS,
  OtherReferenceAttachStatus,
  REFERENCE_MAX_QUANTITY_LIMIT,
  ReferenceLocal,
  REMOVE_REFERENCES,
  REMOVE_SINGLE_REFERENCE,
  REVERT_ATTACH_OTHER_REFERENCE,
  REVERT_LAST_REMOVAL,
  SET_ALL_REFERENCE_URGENCY_FLAGS,
  SET_ORDER_MARK,
  SET_REFERENCE_MARK,
  SET_REFERENCE_URGENCY_FLAG,
  UPDATE_REFERENCE_QUANTITY,
  UPLOAD_FILE_REFERENCES,
  VehicleLocal,
} from 'domains/basket/Basket.types';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { OperationReference } from 'domains/easyBasket/EasyBasket.types';
import { DHReferenceLocal, IAMReferenceLocal, PlateReference, ReferenceTradingDataDetail } from 'domains/references';
import { refWithoutDepositRef } from 'domains/references/DepositReference';
import { isBasketRefExadis, isReferenceEqual, matchesReferenceKey } from 'domains/references/References.utils';
import { FOUND, SEARCH_STATUS } from 'utils';
import { getMaxOrderQuantityForFamily, isSingleUnitOrderRestrictedFamily } from 'utils/rights';
import { getTradingDataMap } from './Basket.mapper';

export function isRefMKTP(reference?: ReferenceLocal): boolean {
  return reference?.referenceSource === 'MARKETPLACE';
}

function sumReferencesPrice(references?: Reference[], includeVat = false): number {
  if (!references || references?.length === 0) {
    return 0;
  }
  return references?.reduce((acc: number, next: Reference) => {
    return (
      acc +
      (includeVat
        ? next?.isUrgent && !isNaN(Number(next?.price?.vatIncludedUrgentDeliveryAdditionalPrice))
          ? Number(next?.price?.priceVatIncluded) + Number(next?.price?.vatIncludedUrgentDeliveryAdditionalPrice)
          : Number(next?.price?.priceVatIncluded)
        : next?.isUrgent && !isNaN(Number(next?.price?.vatExcludedUrgentDeliveryAdditionalPrice))
        ? Number(next?.price?.priceVatExcluded) + Number(next?.price?.vatExcludedUrgentDeliveryAdditionalPrice)
        : Number(next?.price?.priceVatExcluded)) *
        next.quantity
    );
  }, 0);
}

function getMaxQuantityLimitForReference(reference: Reference): number {
  return getMaxOrderQuantityForFamily(
    reference.familyCode,
    isCertasOil(reference.familyCode, reference.productModelCode)
      ? CERTAS_OIL_REFERENCE_QUANTITY_LIMIT
      : REFERENCE_MAX_QUANTITY_LIMIT,
  ) as number;
}

export const computeTotalPrice = (
  vehicles?: VehicleLocal[] | undefined,
  externalBaskets?: ExternalBasketSection[] | undefined,
  otherReferences?: Reference[] | undefined,
  includeVat = false,
): number => {
  const vehiclesTotalPrice = vehicles
    ? vehicles.reduce(
        (acc: number, vehicle: VehicleLocal) => acc + sumReferencesPrice(vehicle?.references, includeVat),
        0,
      )
    : 0;
  const externalsTotalPrice = externalBaskets
    ? externalBaskets.reduce(
        (acc: number, external: ExternalBasketSection) => acc + sumReferencesPrice(external?.references, includeVat),
        0,
      )
    : 0;

  const otherSectionTotalPrice = sumReferencesPrice(otherReferences, includeVat);
  return vehiclesTotalPrice + externalsTotalPrice + otherSectionTotalPrice;
};

function updateReferencesQuantity(
  references: Draft<Reference[]>,
  referenceToUpdate: ReferenceKey,
  newQuantity: number,
): void {
  const referenceIndex = references.findIndex((itemX) => {
    const item = itemX as Reference;
    return (
      item.referenceNumber === referenceToUpdate.referenceNumber && item.sellerNumber === referenceToUpdate.sellerNumber
    );
  });
  if (referenceIndex !== -1) {
    const item = references[referenceIndex];
    const quantityLimit = getMaxQuantityLimitForReference(item);
    references[referenceIndex].quantity = Math.min(Number(newQuantity), quantityLimit);
  }
}

// Init state
const initialState: BasketState = {
  basketId: {
    searchStatus: undefined,
  },
  totalPriceVatIncluded: undefined,
  totalPriceVatExcluded: undefined,
  referenceTradingData: new Map<string, ReferenceTradingDataDetail>(),
  vehicles: [],
  externalBaskets: [],
  actionStatuses: new Map<string, BasketActionStatus>(),
  fileReferencesUploadStatus: { modalStatus: NO_FILE, isModalOpen: false, errorRows: [] },
  otherSection: { references: [], note: '' },
  otherReferenceAttachStatus: new Map<ReferenceKey, OtherReferenceAttachStatus>(),
  expiredDiscountsReferences: [],
  exadisDeliveryMode: false,
};

// Saga actions
export const fetchBasketRequest = createAction(FETCH_BASKET);
export const fetchBasketResponse = createAction(GET_USER_BASKET_RESPONSE);
export const handleInvalidReferenceQuantityUpdate = createAction(BASKET_INVALID_REFERENCE_QUANTITY_UPDATE_RESPONSE);

export const addReferenceRequest = createAction<PlateReference>(ADD_VEHICLE_REFERENCES);
export const addReferenceUnderVehicleRequest = createAction<{
  referenceToAdd: ReferenceKey;
  catalogSource: CatalogSource;
}>(ADD_REFERENCE_BY_REF_NUMBER_UNDER_VEHICLE);
export const addEasyBasketReferenceRequest = createAction<{
  selectedReference: OperationReference;
}>(ADD_REFERENCE_FROM_EASY_BASKET);
export const addTireRequest = createAction<TireItem>(ADD_TIRE_REFERENCE);
export const addOtherReferenceRequest = createAction<{
  reference: string;
  isDH: boolean;
  sellerNumber?: string;
  sellerName?: string;
}>(ADD_OTHER_SECTION_REFERENCE);
export const addOtherSectionReferenceRequest = createAction<{
  reference:
    | (DHReferenceLocal & { isCrossSelling?: boolean; isFromProductPage?: boolean; mainReferenceNumber?: string })
    | (IAMReferenceLocal & { isCrossSelling?: boolean; isFromProductPage?: boolean; mainReferenceNumber?: string })
    | undefined;
}>(ADD_OTHER_SECTION_REFERENCES);
export const addReferenceByRefNumberRequest = createAction<{
  reference: string;
}>(ADD_REFERENCE_BY_REF_NUMBER);
export const crossSellingAddToBasketTracked = createAction<CrossSellingAddToBasketTrackedPayload>(
  CROSS_SELLING_ADD_TO_BASKET_TRACKED,
);
export const addReferenceByRefNumberResponse = createAction(ADD_BASKET_OTHER_REFERENCE_RESPONSE);
export const removeReferencesRequest = createAction<{
  vehiclesWithReferences?: RemoveVehicleReferencesDetail[];
  externalBasketsWithReferences?: RemoveExternalBasketReferencesDetail[];
  otherReferences?: ReferenceKey[];
}>(REMOVE_REFERENCES);
export const removeSingleReferenceRequest = createAction<{
  basketReferenceType: BasketReferenceType;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  referenceKey: ReferenceKey;
}>(REMOVE_SINGLE_REFERENCE);
export const revertLastRemovalRequest = createAction(REVERT_LAST_REMOVAL);
export const updateReferenceQuantityRequest = createAction<{
  basketReferenceType: BasketReferenceType;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  referenceToUpdate: ReferenceKey;
  newQuantity: number;
  origin?: Origin;
  supplierCode?: string;
  referenceSource?: ReferenceSource;
  referenceType: ReferenceType;
  isBulkOilReference: boolean;
}>(UPDATE_REFERENCE_QUANTITY);
export const updateOrderMarkRequest = createAction<{
  basketReferenceType: BasketReferenceType;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  orderMark: string;
}>(SET_ORDER_MARK);
export const updateReferenceMarkRequest = createAction<{
  basketReferenceType: BasketReferenceType;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  referenceToUpdate: ReferenceKey;
  referenceMark: string;
}>(SET_REFERENCE_MARK);
export const uploadFileReferencesRequest = createAction<{
  fileName: string;
  fileBase64: string;
}>(UPLOAD_FILE_REFERENCES);
export const uploadFileReferencesResponse = createAction(UPLOAD_BASKET_REFERENCES_FILE_RESPONSE);
export const updateUrgencyFlagRequest = createAction<{
  basketReferenceType: BasketReferenceType;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  referenceToUpdate: ReferenceKey;
  isUrgent: boolean;
}>(SET_REFERENCE_URGENCY_FLAG);
export const updateAllUrgencyFlagsRequest = createAction<{
  basketReferenceType: BasketReferenceType;
  vehicleKey: string | undefined;
  externalBasketId: string | undefined;
  isUrgent: boolean;
}>(SET_ALL_REFERENCE_URGENCY_FLAGS);
export const attachOtherReferenceToVehicleRequest = createAction<{
  vehicleKey: string;
  reference: ReferenceKey;
}>(ATTACH_OTHER_REFERENCE);
export const attachOtherNonApplicableReferenceToVehicleRequest = createAction<{
  vehicleKey: string | undefined;
  reference: ReferenceKey;
}>(ATTACH_OTHER_NON_APPLICABLE_REFERENCE);
export const confirmMoveNonCompatibleOtherSectionReferenceToVehicleResponse = createAction(
  BASKET_ATTACH_OTHER_REFERENCE_CAR_NONCOMPATIBLE_RESPONSE,
);
export const otherSectionReferenceMovedToVehicleResponse = createAction(BASKET_ATTACH_OTHER_REFERENCE_CAR_RESPONSE);
export const revertMoveOtherReferenceToVehicle = createAction(REVERT_ATTACH_OTHER_REFERENCE);
export const notifyUserAboutExpiredDiscountsRequest = createAction(NOTIFY_USER_ABOUT_EXPIRED_DISCOUNTS);
// Slice
const slice = createSlice({
  name: BASKET_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    resetBasketSubscriptionStatus: (state) => {
      state.basketId.searchStatus = undefined;
    },
    setBasket: (
      state,
      {
        payload,
      }: PayloadAction<{
        basketId: string;
        vehicles?: VehicleSection[];
        otherSection?: OtherSection;
        rememberIsSelected?: boolean;
        expiredReferenceDiscounts?: ExpiredReferenceDiscount[];
        externalBaskets?: ExternalBasketSection[];
        isBulkOilUser: boolean;
        isOilChunkUserR1?: boolean;
      }>,
    ) => {
      const {
        basketId,
        vehicles,
        otherSection,
        rememberIsSelected,
        expiredReferenceDiscounts,
        externalBaskets,
        isBulkOilUser,
      } = payload;
      if (rememberIsSelected) {
        if (vehicles) {
          vehicles.forEach((vehicle) => {
            const localVehicleRaw = state.vehicles as VehicleLocal[] | undefined;
            const localVehicle = localVehicleRaw?.find(
              (v) => v.vehicleDetail.vehicleKey === vehicle.vehicleDetail.vehicleKey,
            );
            vehicle.references = vehicle.references.map((reference) => {
              const localRef = localVehicle?.references?.find(
                (r) => r.referenceNumber === reference.referenceNumber && r.sellerNumber === reference.sellerNumber,
              );
              return {
                ...reference,
                isSelected: localRef?.isSelected ?? true,
              };
            });
          });
        }
        if (otherSection) {
          otherSection.references = otherSection.references.map((reference) => {
            const localRef = state.otherSection?.references.find(
              (r) => r.referenceNumber === reference.referenceNumber && r.sellerNumber === reference.sellerNumber,
            ) as ReferenceLocal;
            return {
              ...reference,
              isSelected: localRef?.isSelected ?? true,
            };
          });
        }
        if (externalBaskets) {
          externalBaskets.forEach((exBasket) => {
            const localExtBasketRaw = state.externalBaskets as ExternalBasketSectionLocal[] | undefined;
            const localExtBasket = localExtBasketRaw?.find((b) => b.externalBasketId === exBasket.externalBasketId);
            exBasket.references = exBasket.references.map((reference) => {
              const localRef = localExtBasket?.references?.find(
                (r) => r.referenceNumber === reference.referenceNumber && r.sellerNumber === reference.sellerNumber,
              );
              return { ...reference, isSelected: localRef?.isSelected ?? true };
            });
          });
        }
        state.expiredDiscountsReferences = expiredReferenceDiscounts ?? [];
      }
      state.vehicles = vehicles;
      if (otherSection) state.otherSection = otherSection;
      if (externalBaskets) state.externalBaskets = externalBaskets;
      state.basketId.data = basketId;
      state.basketId.searchStatus = FOUND;
      state.referenceTradingData = getTradingDataMap(
        vehicles,
        externalBaskets,
        otherSection,
        isBulkOilUser,
        payload.isOilChunkUserR1,
      );
    },
    updateTradingDataMap: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicles?: VehicleSection[];
        otherSection?: OtherSection;
        externalBaskets?: ExternalBasketSection[];
        isBulkOilUser: boolean;
        isOilChunkUserR1?: boolean;
      }>,
    ) => {
      const { vehicles, otherSection, externalBaskets, isBulkOilUser } = payload;
      state.referenceTradingData = getTradingDataMap(
        vehicles,
        externalBaskets,
        otherSection,
        isBulkOilUser,
        payload.isOilChunkUserR1,
      );
    },
    setTotalPrices: (
      state,
      {
        payload,
      }: PayloadAction<{
        vatIncluded: number;
        vatExcluded: number;
        totalDiscount: number;
      }>,
    ) => {
      state.totalPriceVatIncluded = payload.vatIncluded;
      state.totalPriceVatExcluded = payload.vatExcluded;
      state.totalDiscount = payload.totalDiscount;
    },
    addVehicleReferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleDetail: VehicleDetail;
        references: ReferenceLocal[];
      }>,
    ) => {
      const { vehicleDetail, references } = payload;
      const vehicleKey = vehicleDetail?.vehicleKey;
      const index = state.vehicles?.findIndex((searchedVehicle) => {
        return searchedVehicle?.vehicleDetail?.vehicleKey === vehicleKey;
      }) as number;

      if (index === -1) {
        const validatedReferences = references.map((ref) => ({
          ...ref,
          quantity: Math.min(ref.quantity, getMaxQuantityLimitForReference(ref)),
        }));
        const newVehicle: VehicleLocal = {
          vehicleDetail,
          references: validatedReferences,
        };
        state.vehicles?.push(newVehicle);
      } else {
        if (state.vehicles) {
          const vehicle = state.vehicles[index];

          references?.forEach((ref) => {
            if (vehicle.references) {
              const referenceIndex = vehicle.references.findIndex(
                (item) => item.referenceNumber === ref.referenceNumber && item.sellerNumber === ref.sellerNumber,
              );
              if (referenceIndex !== -1) {
                const oldQuantity = vehicle.references[referenceIndex].quantity ?? 0;
                vehicle.references[referenceIndex] = {
                  ...ref,
                  quantity: Math.min(ref.quantity + oldQuantity, getMaxQuantityLimitForReference(ref)),
                };
              } else {
                vehicle.references.push(ref);
              }
            }
          });
        }
      }

      state.totalPriceVatIncluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        true,
      );
      state.totalPriceVatExcluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        false,
      );
    },
    addOtherReference: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference: ReferenceLocal;
      }>,
    ) => {
      const { reference } = payload;
      const otherReferences = state.otherSection.references;
      const referenceIndex = otherReferences.findIndex(
        (item) => item.referenceNumber === reference.referenceNumber && item.sellerNumber === reference.sellerNumber,
      );

      if (referenceIndex !== -1) {
        const oldQuantity = otherReferences[referenceIndex].quantity;
        const maxQuantityLimit = getMaxQuantityLimitForReference(reference);
        otherReferences[referenceIndex].quantity = Math.min(reference.quantity + oldQuantity, maxQuantityLimit);
      } else {
        otherReferences.push(reference);
      }

      state.totalPriceVatIncluded = computeTotalPrice(state.vehicles, state.externalBaskets, otherReferences, true);
      state.totalPriceVatExcluded = computeTotalPrice(state.vehicles, state.externalBaskets, otherReferences, false);
    },
    removeReference: (
      state,
      {
        payload,
      }: PayloadAction<{
        basketReferenceType: BasketReferenceType;
        referenceToRemove: ReferenceKey;
        vehicleKey?: string;
        externalBasketId?: string;
        sellerNumber?: string;
      }>,
    ) => {
      const { referenceToRemove, vehicleKey, basketReferenceType, externalBasketId } = payload;

      switch (basketReferenceType) {
        case 'VEHICLE': {
          if (vehicleKey) {
            const index = state.vehicles?.findIndex(({ vehicleDetail }) => vehicleDetail?.vehicleKey === vehicleKey);
            if (index === -1 || typeof index !== 'number') return;
            const vehicle = state.vehicles?.[index];
            if (!vehicle) return;

            vehicle.references =
              vehicle.references?.filter(
                (ref) =>
                  referenceToRemove.referenceNumber !== ref.referenceNumber ||
                  referenceToRemove.sellerNumber !== ref.sellerNumber,
              ) ?? [];
            // remove vehicle from basket if the vehicle has no references
            state.vehicles = state.vehicles?.filter((v) => v?.references && v.references.length > 0);
          }
          break;
        }
        case 'OTHER': {
          if (!state.otherSection || !state.otherSection.references) return;
          state.otherSection.references = state.otherSection.references.filter(
            (ref) =>
              referenceToRemove.referenceNumber !== ref.referenceNumber ||
              referenceToRemove.sellerNumber !== ref.sellerNumber,
          );
          break;
        }
        case 'EXTERNAL_BASKET': {
          if (externalBasketId) {
            const index = state.externalBaskets?.findIndex(
              (exBasket) => exBasket?.externalBasketId === externalBasketId,
            );
            if (index === -1 || typeof index !== 'number') return;
            const exBasket = state.externalBaskets?.[index];
            if (!exBasket) return;

            exBasket.references =
              exBasket.references?.filter(
                (ref) =>
                  referenceToRemove.referenceNumber !== ref.referenceNumber ||
                  referenceToRemove.sellerNumber !== ref.sellerNumber,
              ) ?? [];
            // remove exBasket from basket if the exBasket has no references
            state.vehicles = state.vehicles?.filter((v) => v?.references && v.references.length > 0);
          }
          break;
        }
      }

      state.totalPriceVatIncluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        true,
      );
      state.totalPriceVatExcluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        false,
      );
    },
    removeReferences: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehiclesWithReferences?: RemoveVehicleReferencesDetail[];
        otherReferences?: ReferenceKey[];
        externalBasketsWithReferences?: RemoveExternalBasketReferencesDetail[];
      }>,
    ) => {
      const { vehiclesWithReferences, otherReferences, externalBasketsWithReferences } = payload;
      if (Array.isArray(vehiclesWithReferences)) {
        vehiclesWithReferences.forEach((vehicleWithReferences: RemoveVehicleReferencesDetail) => {
          const index = state.vehicles?.findIndex(
            ({ vehicleDetail }) => vehicleDetail?.vehicleKey === vehicleWithReferences.vehicleKey,
          );
          if (index === -1 || typeof index !== 'number') return;
          const vehicle = state.vehicles?.[index];
          if (!vehicle) return;

          vehicle.references = vehicle.references.filter(
            (ref) =>
              !vehicleWithReferences.references.some((refId) =>
                isReferenceEqual(
                  {
                    referenceNumber: ref.referenceNumber,
                    sellerNumber: ref.sellerNumber,
                  },
                  refId,
                ),
              ),
          );
          state.vehicles = state.vehicles?.filter((v) => v?.references && v.references.length > 0);
        });
      }

      if (Array.isArray(otherReferences) && state.otherSection?.references) {
        state.otherSection.references = state.otherSection.references.filter(
          (ref) =>
            !otherReferences.some((refId) =>
              isReferenceEqual(
                {
                  referenceNumber: ref.referenceNumber,
                  sellerNumber: ref.sellerNumber,
                },
                refId,
              ),
            ),
        );
      }

      if (Array.isArray(externalBasketsWithReferences)) {
        externalBasketsWithReferences.forEach((extBasketDetail: RemoveExternalBasketReferencesDetail) => {
          const index = state.externalBaskets?.findIndex(
            (exBasket) => exBasket?.externalBasketId === extBasketDetail.externalBasketId,
          );
          if (index === -1 || typeof index !== 'number') return;
          const exBasket = state.externalBaskets?.[index];
          if (!exBasket) return;

          exBasket.references = exBasket.references?.filter(
            (ref) => !extBasketDetail.references.includes(ref.referenceNumber),
          );
          state.externalBaskets = state.externalBaskets?.filter((v) => v?.references && v.references.length > 0);
        });
      }

      state.totalPriceVatIncluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        true,
      );
      state.totalPriceVatExcluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        false,
      );
    },
    setReferencesIsSelected: (
      state,
      {
        payload,
      }: PayloadAction<{
        isSelected: boolean;
        referenceIds: ReferenceKey[];
        vehicleKey?: string;
        externalBasketId?: string;
      }>,
    ) => {
      const { isSelected, referenceIds, vehicleKey, externalBasketId } = payload;
      if (vehicleKey) {
        const vehicles = state.vehicles as VehicleLocal[];
        state.vehicles = vehicles.map((v) => {
          if (v.vehicleDetail.vehicleKey === vehicleKey) {
            const newReferences = v.references?.map((ref) =>
              referenceIds.some((refId) =>
                isReferenceEqual(
                  {
                    referenceNumber: ref.referenceNumber,
                    sellerNumber: ref.sellerNumber,
                  },
                  refId,
                ),
              )
                ? { ...ref, isSelected }
                : ref,
            );
            return { ...v, references: newReferences };
          } else {
            return v;
          }
        });
      } else if (externalBasketId) {
        const externalBaskets = state.externalBaskets;
        state.externalBaskets = externalBaskets.map((exBasket) => {
          if (exBasket.externalBasketId === externalBasketId) {
            const newReferences = exBasket.references.map((ref) =>
              referenceIds.some((refId) =>
                isReferenceEqual(
                  {
                    referenceNumber: ref.referenceNumber,
                    sellerNumber: ref.sellerNumber,
                  },
                  refId,
                ),
              )
                ? { ...ref, isSelected }
                : ref,
            );
            return { ...exBasket, references: newReferences };
          } else {
            return exBasket;
          }
        });
      } else {
        if (!state.otherSection || !state.otherSection.references) return;
        state.otherSection.references = state.otherSection.references.map((ref) =>
          referenceIds.some((refId) =>
            isReferenceEqual(
              {
                referenceNumber: ref.referenceNumber,
                sellerNumber: ref.sellerNumber,
              },
              refId,
            ),
          )
            ? { ...ref, isSelected }
            : ref,
        );
      }
    },
    setReferenceDeliveryType: (
      state,
      {
        payload,
      }: PayloadAction<{
        deliveryType: OrderDeliveryType | undefined;
        referenceId: ReferenceKey;
        vehicleKey?: string;
        externalBasketId?: string;
      }>,
    ) => {
      const { deliveryType, referenceId, vehicleKey, externalBasketId } = payload;
      if (vehicleKey) {
        const vehicles = state.vehicles as VehicleLocal[];
        state.vehicles = vehicles.map((v) => {
          if (v.vehicleDetail.vehicleKey === vehicleKey) {
            const newReferences = v.references?.map((ref) =>
              referenceId.referenceNumber === ref.referenceNumber && referenceId.sellerNumber === ref.sellerNumber
                ? {
                    ...ref,
                    deliveryType,
                  }
                : ref,
            );
            return { ...v, references: newReferences };
          } else {
            return v;
          }
        });
      } else if (externalBasketId) {
        const externalBaskets = state.externalBaskets;
        state.externalBaskets = externalBaskets.map((exBasket) => {
          if (exBasket.externalBasketId === externalBasketId) {
            const newReferences = exBasket.references.map((ref) =>
              matchesReferenceKey(ref, referenceId)
                ? {
                    ...ref,
                    deliveryType,
                  }
                : ref,
            );
            return { ...exBasket, references: newReferences };
          } else {
            return exBasket;
          }
        });
      } else {
        if (!state.otherSection?.references) return;
        state.otherSection.references = state.otherSection.references.map((ref) =>
          referenceId.referenceNumber === ref.referenceNumber && referenceId.sellerNumber === ref.sellerNumber
            ? {
                ...ref,
                deliveryType,
              }
            : ref,
        );
      }
    },
    resetReferencesDeliveryType: (state) => {
      state.vehicles = state.vehicles?.map((v) => {
        const newReferences = v.references?.map((ref) => {
          return { ...ref, deliveryType: undefined };
        });
        return { ...v, references: newReferences };
      });
      state.externalBaskets = state.externalBaskets?.map((v) => {
        const newReferences = v.references?.map((ref) => {
          return { ...ref, deliveryType: undefined };
        });
        return { ...v, references: newReferences };
      });

      if (!state.otherSection?.references) return;
      state.otherSection.references = state.otherSection.references.map((ref) => {
        return { ...ref, deliveryType: undefined };
      });
    },
    updateReferenceQuantity: (
      state,
      {
        payload,
      }: PayloadAction<{
        referenceToUpdate: ReferenceKey;
        newQuantity: number;
        vehicleKey: string | undefined;
        externalBasketId: string | undefined;
      }>,
    ) => {
      const { referenceToUpdate, newQuantity, vehicleKey, externalBasketId } = payload;
      if (vehicleKey) {
        const index = state.vehicles?.findIndex(({ vehicleDetail }) => vehicleDetail?.vehicleKey === vehicleKey);
        if (index === -1 || typeof index !== 'number') return;
        const vehicle = state.vehicles?.[index];
        if (!vehicle || !vehicle.references) {
          return;
        }
        updateReferencesQuantity(vehicle.references, referenceToUpdate, newQuantity);
      } else if (externalBasketId) {
        const externalBasket = state.externalBaskets?.find((e) => e.externalBasketId === externalBasketId);
        if (!externalBasket) {
          return;
        }
        updateReferencesQuantity(externalBasket.references, referenceToUpdate, newQuantity);
      } else {
        if (!state.otherSection || !state.otherSection.references) {
          return;
        }
        updateReferencesQuantity(state.otherSection.references, referenceToUpdate, newQuantity);
      }

      state.totalPriceVatIncluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        true,
      );
      state.totalPriceVatExcluded = computeTotalPrice(
        state.vehicles,
        state.externalBaskets,
        state.otherSection?.references,
        false,
      );
    },
    updateReferenceUrgencyFlag: (
      state,
      {
        payload,
      }: PayloadAction<{
        vehicleKey: string | undefined;
        externalBasketId: string | undefined;
        referencesToUpdate: ReferenceKey[];
        isUrgent: boolean;
      }>,
    ) => {
      const { vehicleKey, referencesToUpdate, isUrgent, externalBasketId } = payload;
      if (vehicleKey) {
        const vehicles = state.vehicles as VehicleLocal[];
        state.vehicles = vehicles.map((veh) => {
          if (veh.vehicleDetail.vehicleKey === vehicleKey) {
            const newReferences = veh.references?.map((ref) =>
              referencesToUpdate.some((refId) =>
                isReferenceEqual(
                  {
                    referenceNumber: ref.referenceNumber,
                    sellerNumber: ref.sellerNumber,
                  },
                  refId,
                ),
              )
                ? { ...ref, isUrgent }
                : ref,
            );
            return { ...veh, references: newReferences };
          } else {
            return veh;
          }
        });
      } else if (externalBasketId) {
        const extBasket = state.externalBaskets;
        state.externalBaskets = extBasket.map((eb) => {
          if (eb.externalBasketId === externalBasketId) {
            const newReferences = eb.references?.map((ref) =>
              referencesToUpdate.some((refId) =>
                isReferenceEqual(
                  {
                    referenceNumber: ref.referenceNumber,
                    sellerNumber: ref.sellerNumber,
                  },
                  refId,
                ),
              )
                ? { ...ref, isUrgent }
                : ref,
            );
            return { ...eb, references: newReferences };
          } else {
            return eb;
          }
        });
      } else {
        if (!state.otherSection || !state.otherSection.references) return;
        state.otherSection.references = state.otherSection.references.map((ref) =>
          referencesToUpdate.some((refId) =>
            isReferenceEqual(
              {
                referenceNumber: ref.referenceNumber,
                sellerNumber: ref.sellerNumber,
              },
              refId,
            ),
          )
            ? { ...ref, isUrgent }
            : ref,
        );
      }
    },
    setActionStatus: (state, { payload }: PayloadAction<BasketActionStatus>) => {
      state.actionStatuses.set(payload.reference, payload);
    },
    setExadisDeliveryMode: (state, { payload }: PayloadAction<boolean>) => {
      state.exadisDeliveryMode = payload;
    },
    clearActionStatuses: (state) => {
      state.actionStatuses.clear();
    },
    setFileReferencesUploadStatusModalStatus: (state, { payload }: PayloadAction<ModalStatusType>) => {
      state.fileReferencesUploadStatus.modalStatus = payload;
    },
    setFileReferencesUploadStatusIsModalOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.fileReferencesUploadStatus.isModalOpen = payload;
    },
    setFileReferencesUploadStatusErrorRows: (state, { payload }: PayloadAction<ReferenceErrorRow[]>) => {
      state.fileReferencesUploadStatus.errorRows = [...(payload ?? [])].sort((a, b) =>
        a.rowNumber > b.rowNumber ? 1 : -1,
      );
    },
    setBasketIdStatus: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.basketId.searchStatus = payload;
    },
    setOtherReferenceAttachStatus: (state, { payload }: PayloadAction<OtherReferenceAttachStatus>) => {
      state.otherReferenceAttachStatus.set(payload.reference, payload);
    },
    clearAllOtherReferenceAttachStatus: (state) => {
      state.otherReferenceAttachStatus.clear();
    },
    clearOtherReferenceAttachStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        // vehicleKey: string | undefined;
        reference: ReferenceKey;
      }>,
    ) => {
      const { reference } = payload;
      state.otherReferenceAttachStatus.delete(reference);
    },
    moveOtherReferenceToVehicle: (
      state,
      {
        payload,
      }: PayloadAction<{
        reference?: ReferenceKey;
        vehicleKey?: string;
        isCompatible?: boolean;
      }>,
    ) => {
      const { reference, vehicleKey, isCompatible } = payload;
      if (vehicleKey && reference) {
        //get other
        const referenceToMove = state.otherSection.references.filter(
          (ref) => reference.referenceNumber === ref.referenceNumber && reference.sellerNumber === ref.sellerNumber,
        );
        //add to vehicle
        const index = state.vehicles?.findIndex(({ vehicleDetail }) => vehicleDetail?.vehicleKey === vehicleKey);
        //must be existing car in basket
        if (index === -1 || typeof index !== 'number') return;
        const vehicle = state.vehicles?.[index];
        if (!vehicle) return;

        referenceToMove?.forEach((ref) => {
          if (vehicle.references) {
            const referenceIndex = vehicle.references.findIndex(
              (item) => item.referenceNumber === ref.referenceNumber && item.sellerNumber === ref.sellerNumber,
            );
            if (referenceIndex !== -1) {
              const oldQuantity = vehicle.references[referenceIndex].quantity ?? 0;
              vehicle.references[referenceIndex] = {
                ...ref,
                quantity: Math.min(ref.quantity + oldQuantity, getMaxQuantityLimitForReference(ref)),
                isCompatible: vehicle.references[referenceIndex].isCompatible,
              };
            } else {
              ref.isCompatible = isCompatible;
              vehicle.references.push(ref);
            }
          }
        });

        //remove from other
        state.otherSection.references = state.otherSection.references.filter(
          (ref) => reference.referenceNumber !== ref.referenceNumber || reference.sellerNumber !== ref.sellerNumber,
        );
      }
    },
    clearExpiredDiscountsReferences: (state) => {
      state.expiredDiscountsReferences = [];
    },
  },
});
// Actions
export const {
  setInitialState,
  resetBasketSubscriptionStatus,
  setBasket,
  setTotalPrices,
  addVehicleReferences,
  addOtherReference,
  removeReference,
  removeReferences,
  setReferencesIsSelected,
  setReferenceDeliveryType,
  updateReferenceQuantity,
  updateReferenceUrgencyFlag,
  setActionStatus,
  resetReferencesDeliveryType,
  clearActionStatuses,
  setFileReferencesUploadStatusModalStatus,
  setFileReferencesUploadStatusIsModalOpen,
  setFileReferencesUploadStatusErrorRows,
  setBasketIdStatus,
  setOtherReferenceAttachStatus,
  clearOtherReferenceAttachStatus,
  moveOtherReferenceToVehicle,
  clearExpiredDiscountsReferences,
  updateTradingDataMap,
  setExadisDeliveryMode,
} = slice.actions;

// Getters/Selectors
export const getBasketVehicles = createSelector(
  (state: RootState) => state.basket.vehicles,
  (vehicles) => {
    return vehicles ?? [];
  },
);

export const getBasketOtherSection = createSelector(
  (state: RootState) => state.basket.otherSection,
  (otherSection) => otherSection,
);

export const getBasketExadisDeliveryMode = createSelector(
  (state: RootState) => state.basket.exadisDeliveryMode,
  (exadisDeliveryMode) => exadisDeliveryMode,
);

export const getBasketExternalSection = createSelector(
  (state: RootState) => state.basket.externalBaskets,
  (externalBaskets) => externalBaskets,
);

export const getBasketReference = createSelector(
  getBasketVehicles,
  getBasketOtherSection,
  (
    _state: RootState,
    payload: {
      reference: ReferenceKey;
      vehicleKey?: string;
    },
  ) => payload,
  (vehicles, otherSection, payload) => {
    const { reference, vehicleKey } = payload;
    let allReferences = otherSection.references;
    if (vehicleKey !== undefined) {
      const vehicleReferences = vehicles.find((vehicle) => vehicle.vehicleDetail.vehicleKey === vehicleKey)?.references;
      if (vehicleReferences !== undefined) {
        allReferences = [...allReferences, ...vehicleReferences];
      }
    }
    return allReferences?.find((ref) => matchesReferenceKey(ref, reference));
  },
);

export const getReferenceFromBasket = createSelector(
  getBasketVehicles,
  getBasketOtherSection,
  (_state: RootState, reference?: ReferenceKey) => reference,
  (vehicles, otherSection, reference) => {
    let allReferences = otherSection.references;
    const vehicleReferences = vehicles.flatMap((vehicle) => vehicle.references);
    if (vehicleReferences !== undefined) {
      allReferences = [...allReferences, ...vehicleReferences];
    }
    return allReferences?.find(
      (ref) => ref.referenceNumber === reference?.referenceNumber && ref.sellerNumber === reference?.sellerNumber,
    );
  },
);

export const getBasketPrices = createSelector(
  (state: RootState) => state.basket.totalPriceVatIncluded,
  (state: RootState) => state.basket.totalPriceVatExcluded,
  (state: RootState) => state.basket.totalDiscount,
  (totalPriceVatIncluded, totalPriceVatExcluded, totalDiscount) => {
    return {
      totalPriceVatIncluded,
      totalPriceVatExcluded,
      totalDiscount,
    };
  },
);

export const getActionStatus = createSelector(
  (state: RootState) => state.basket.actionStatuses,
  (_state: RootState, reference: string | undefined) => reference,
  (actionStatuses, reference) => {
    return reference ? actionStatuses.get(reference) : undefined;
  },
);

export const getFileReferencesUploadStatusModalStatus = createSelector(
  (state: RootState) => state.basket.fileReferencesUploadStatus.modalStatus,
  (modalStatus) => modalStatus,
);

export const getFileReferencesUploadStatusIsModalOpen = createSelector(
  (state: RootState) => state.basket.fileReferencesUploadStatus.isModalOpen,
  (isModalOpen) => isModalOpen,
);

export const getFileReferencesUploadStatusErrorRows = createSelector(
  (state: RootState) => state.basket.fileReferencesUploadStatus.errorRows,
  (errorRows) => errorRows,
);

export const getReferenceQuantity = createSelector(
  (state: RootState) => state.basket.vehicles,
  (state: RootState) => state.basket.otherSection.references,
  getLastSearchedVehicleKey,
  (
    _state: RootState,
    payload: {
      reference?: ReferenceKey;
      isApplicableToCurrentVehicle?: boolean;
    },
  ) => payload,
  (vehicles, otherReferences, lastSearchedVehicleKey, payload) => {
    if (!payload.reference) return 0;
    const basketReferences =
      payload.isApplicableToCurrentVehicle && lastSearchedVehicleKey
        ? vehicles?.find((vehicle) => vehicle.vehicleDetail.vehicleKey === lastSearchedVehicleKey)?.references
        : otherReferences;
    return (
      basketReferences?.find(
        (ref) =>
          ref.referenceNumber === payload.reference?.referenceNumber &&
          ref.sellerNumber === payload.reference?.sellerNumber,
      )?.quantity ?? 0
    );
  },
);

export const getBasketId = createSelector(
  (state: RootState) => state.basket.basketId.data,
  (data) => data,
);

export const getBasketIdStatus = createSelector(
  (state: RootState) => state.basket.basketId.searchStatus,
  (searchStatus) => searchStatus,
);

export const getOtherReferenceAttachStatus = createSelector(
  (state: RootState) => state.basket.otherReferenceAttachStatus,
  (_state: RootState, status: string) => status,
  (otherReferenceAttachStatuses, filterStatus) => {
    return [...otherReferenceAttachStatuses.values()].filter((status) => status.type === filterStatus);
  },
);

export const getBasketNumberOfItems = createSelector(
  (state: RootState) => state.basket?.otherSection,
  (state: RootState) => state.basket?.externalBaskets,
  (state: RootState) => state.basket?.vehicles,
  (otherSection, externalBaskets, vehicles) =>
    (refWithoutDepositRef(otherSection?.references).length ?? 0) +
    (externalBaskets?.reduce((acc, next) => acc + (refWithoutDepositRef(next?.references).length ?? 0), 0) ?? 0) +
    (vehicles?.reduce((acc, next) => acc + (refWithoutDepositRef(next?.references).length ?? 0), 0) ?? 0),
);

export const getBasketReferenceTradingData = createSelector(
  (state: RootState) => state.basket.referenceTradingData,
  (referenceTradingData) => referenceTradingData,
);

export const getExpiredDiscountsReferences = createSelector(
  (state: RootState) => state.basket.expiredDiscountsReferences,
  (expiredDiscountsReferences) => expiredDiscountsReferences,
);

export const getBasketHasSelectedMKTPReference = createSelector(
  (state: RootState) => state.basket?.vehicles,
  (state: RootState) => state.basket?.otherSection,
  (state: RootState) => state.basket?.externalBaskets,
  (vehicles, otherSection, externalBaskets) => {
    let ret = false;
    vehicles?.forEach((vehicle) => {
      vehicle.references.forEach((refNumber) => {
        if (isRefMKTP(refNumber) && refNumber.isSelected) {
          ret = true;
        }
      });
    });
    otherSection.references.forEach((refNumber) => {
      if (isRefMKTP(refNumber) && refNumber.isSelected) {
        ret = true;
      }
    });
    externalBaskets?.forEach((exBasket) => {
      exBasket.references.forEach((refNumber) => {
        if (isRefMKTP(refNumber) && refNumber.isSelected) {
          ret = true;
        }
      });
    });
    return ret;
  },
);

export const getBasketIAMWithoutMotrioReferencesCount = createSelector(
  (state: RootState) => state.basket?.vehicles,
  (state: RootState) => state.basket?.otherSection,
  (state: RootState) => state.basket?.externalBaskets,
  (vehicles, otherSection, externalBaskets) => {
    let iamRefCount = 0;
    vehicles?.forEach((vehicle) => {
      vehicle.references.forEach((refNumber) => {
        if (isBasketRefExadis(refNumber) && refNumber.isSelected) {
          iamRefCount++;
        }
      });
    });
    otherSection.references.forEach((refNumber) => {
      if (isBasketRefExadis(refNumber) && refNumber.isSelected) {
        iamRefCount++;
      }
    });
    externalBaskets?.forEach((exBasket) => {
      exBasket.references.forEach((refNumber) => {
        if (isBasketRefExadis(refNumber) && refNumber.isSelected) {
          iamRefCount++;
        }
      });
    });
    return iamRefCount;
  },
);

export const getBasketIAMWithoutMotrioReferences = createSelector(
  (state: RootState) => state.basket?.vehicles,
  (state: RootState) => state.basket?.otherSection,
  (state: RootState) => state.basket?.externalBaskets,
  (vehicles, otherSection, externalBaskets) => {
    const iamRef: ReferenceLocal[] = [];
    vehicles?.forEach((vehicle) => {
      vehicle.references.forEach((refNumber) => {
        if (isBasketRefExadis(refNumber) && refNumber.isSelected) {
          iamRef.push(refNumber);
        }
      });
    });
    otherSection.references.forEach((refNumber) => {
      if (isBasketRefExadis(refNumber) && refNumber.isSelected) {
        iamRef.push(refNumber);
      }
    });
    externalBaskets?.forEach((exBasket) => {
      exBasket.references.forEach((refNumber) => {
        if (isBasketRefExadis(refNumber) && refNumber.isSelected) {
          iamRef.push(refNumber);
        }
      });
    });
    return iamRef;
  },
);

export const getBasketHasTractionBatteryForVehicle = createSelector(
  (state: RootState) => state.basket?.vehicles,
  (_state: RootState, vehicleKey?: string) => vehicleKey,
  (vehicles, vehicleKey) => {
    if (!vehicleKey) {
      return false;
    }
    const vehicle = vehicles?.find((v) => v.vehicleDetail?.vehicleKey === vehicleKey);
    return vehicle?.references?.some((ref) => isSingleUnitOrderRestrictedFamily(ref.familyCode)) ?? false;
  },
);
// Export reducer
export default slice.reducer;
